// Sheet liviano "Agregar pose" — se abre desde el "+" de un grupo de
// producto en la pantalla de revisión del catálogo (CatalogoCardsFullscreen).
// Muestra las poses AGREGABLES para ese producto (ya filtradas por el padre:
// compatibles + que aún no están), multi-select, y al confirmar las suma.
// Borrar sigue siendo con la X de cada card; este sheet SOLO agrega.
//
// Portaleado a document.body con z por encima del overlay full-screen del
// review (z-[110]).

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, X } from 'lucide-react';
import CachedImg from '../../../components/CachedImg';
import { resolveVistaImageUrl, type VistaCatalogo } from '../vistasCatalogo';

export interface AgregarPoseSheetProps {
  open: boolean;
  /** Nombre del producto al que se le agregan poses (para el subtítulo). */
  productName: string;
  /** model_view del producto, para resolver el thumbnail correcto de cada
   *  vista (override de ropa, etc.). */
  productModelView: string | null;
  /** Poses agregables ya filtradas por el padre (compatibles + no presentes). */
  poses: VistaCatalogo[];
  onConfirm: (selected: VistaCatalogo[]) => void;
  onClose: () => void;
  /**
   * Qué se está agregando. En el Catálogo de muebles no hay poses — son las 3
   * vistas del modo — y llamarlas "pose" confundía. Default 'pose' para no
   * cambiar el Catálogo IA.
   */
  etiqueta?: 'pose' | 'vista';
}

const sameVista = (a: VistaCatalogo, b: VistaCatalogo) =>
  a.kind === b.kind && a.id === b.id;

export const AgregarPoseSheet: React.FC<AgregarPoseSheetProps> = ({
  open,
  productName,
  productModelView,
  poses,
  onConfirm,
  onClose,
  etiqueta = 'pose',
}) => {
  const [selected, setSelected] = useState<VistaCatalogo[]>([]);
  // Reset de la selección cada vez que se abre.
  useEffect(() => {
    if (open) setSelected([]);
  }, [open]);

  if (!open) return null;

  const isSel = (v: VistaCatalogo) => selected.some((s) => sameVista(s, v));
  const toggle = (v: VistaCatalogo) =>
    setSelected((prev) =>
      prev.some((s) => sameVista(s, v))
        ? prev.filter((s) => !sameVista(s, v))
        : [...prev, v],
    );

  return createPortal(
    <div
      className="fixed inset-0 z-[130] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[85vh] flex flex-col overflow-hidden animate-mode-swap"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-black/[0.06]">
          <div className="min-w-0">
            <h3 className="text-[16px] font-semibold text-brand-black/90 leading-tight">
              {etiqueta === 'vista' ? 'Agregar vista' : 'Agregar pose'}
            </h3>
            <p className="text-[12.5px] text-[#3C3C43]/60 truncate">
              Para {productName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-black/[0.04] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Grilla de poses agregables */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {poses.length === 0 ? (
            <p className="text-center text-[#3C3C43]/55 py-12 text-[14px]">
              No hay más {etiqueta === 'vista' ? 'vistas' : 'poses'} para
              agregar a este producto.
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
              {poses.map((v) => {
                const thumb = resolveVistaImageUrl(v, productModelView);
                const sel = isSel(v);
                return (
                  <button
                    key={`${v.kind}-${v.id}`}
                    type="button"
                    onClick={() => toggle(v)}
                    className={`relative aspect-[3/4] rounded-xl overflow-hidden bg-neutral-100 active:scale-[0.98] transition-all ${
                      sel
                        ? 'ring-2 ring-brand-primary'
                        : 'ring-1 ring-black/[0.06] hover:ring-black/[0.12]'
                    }`}
                  >
                    {thumb ? (
                      <CachedImg
                        src={thumb}
                        alt={v.nombre}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-[#3C3C43]/40 text-[12.5px] text-center px-2">
                        {v.nombre}
                      </div>
                    )}
                    {/* Gradiente + nombre blanco superpuesto — mismo patrón
                        que las cards del picker de poses (VistaPickerFullscreen). */}
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                    <p className="pointer-events-none absolute bottom-2 left-2 right-2 text-left text-white text-[12.5px] font-medium leading-tight drop-shadow">
                      {v.nombre}
                    </p>
                    {sel && (
                      <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-brand-primary text-white inline-flex items-center justify-center">
                        <Check className="w-3.5 h-3.5" strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-5 pt-3 border-t border-black/[0.06]"
          style={{
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
          }}
        >
          <button
            type="button"
            disabled={selected.length === 0}
            onClick={() => onConfirm(selected)}
            className="w-full inline-flex items-center justify-center py-3 rounded-2xl bg-brand-primary disabled:opacity-40 disabled:cursor-not-allowed hover:bg-brand-primary-hover text-white text-[15px] font-semibold transition-colors"
          >
            {selected.length > 0 ? `Agregar (${selected.length})` : 'Agregar'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
